import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";

export interface CustomerItem {
  profile_id: number;
  username: string;
  mobile_number: string;
}

type Props = {
  items: CustomerItem[];
  value?: string;
  placeholder?: string;
  onChange: (value: string | undefined, selected?: CustomerItem | undefined) => void;
  clearOnSelect?: boolean;
  maxSuggestions?: number;
};

export default function AutocompleteCustomer({
  items,
  value,
  placeholder = "Select customer...",
  onChange,
  clearOnSelect = false,
  maxSuggestions = 10,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (value) {
      const sel = items.find((i) => String(i.profile_id) === value);
      setQuery(sel ? `${sel.username} ` : "");
    } else {
      setQuery("");
    }
  }, [value, items]);

  const normalized = query.trim().toLowerCase();
  const filtered = items
    .filter((i) => {
      if (!normalized) return true;
      return i.username.toLowerCase().includes(normalized) || i.mobile_number.toLowerCase().includes(normalized);
    })
    .slice(0, maxSuggestions);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  function handleSelect(item: CustomerItem) {
    onChange(String(item.profile_id), item);
    setOpen(false);
    setQuery(clearOnSelect ? "" : `${item.username} — ${item.mobile_number}`);
    inputRef.current?.blur();
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 border bg-white cursor-text w-full rounded-md border-gray-200 bg-white px-3 py-2 shadow-sm placeholder-gray-400  transition`}
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(undefined, undefined);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1 outline-none text-sm bg-white "
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              onChange(undefined, undefined);
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="text-gray-400 hover:text-gray-600 transition cursor-pointer rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            <span><Icon icon="jam:close" width="18" /></span>
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border-2 border-yellow-200 rounded-lg shadow-lg max-h-64 overflow-auto animate-fade-in scrollbar-thin-gray">
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">No customers</div>
          ) : (
            filtered.map((c, idx) => (
              <button
                key={c.profile_id}
                type="button"
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => handleSelect(c)}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 transition ${
                  idx === highlight ? "bg-indigo-50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 text-xs font-semibold">
                  {initials(c.username)}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{c.username}</div>
                  <div className="text-xs text-gray-500">{c.mobile_number}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function initials(name: string) {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "U";
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
